import { FAST_DATA_STALE_TIME_MSEC, SLOW_DATA_STALE_TIME_MSEC, } from '../../constants';
import { endpointVuln } from '..';
import { useOdataPostSingleApi, useODataReadInfiniteApi, } from '../odata';
import { endpointConversation, endpointMessage, getEndpointConversationMessage, getEndpointMessage, } from './endpoint';
import { merge } from '@fluentui/react';
/** Odata API for accessing message data for a specific vuln report. */
export const useMessageFromReportNumberInfiniteApi = (reportNumber, query, options = {}) => useMessageInfiniteApi(merge({}, query, {
    filter: {
        reportNumber: { data: `'${reportNumber}'` },
    },
}), merge({}, options, {
    enabled: !!reportNumber && reportNumber.length > 0,
}));
/** Odata API for accessing message data. */
export const useMessageInfiniteApi = (query, options = {}) => useODataReadInfiniteApi(endpointMessage, query, merge({}, {
    useQueryOptions: {
        // This should poll often to create the feeling of real-time chat
        staleTime: FAST_DATA_STALE_TIME_MSEC,
        // Keep the data cached to ease navigation
        cacheTime: SLOW_DATA_STALE_TIME_MSEC,
        // This should stay fresh if the user is focusing elsewhere
        refetchIntervalInBackground: true,
    },
}, options, {
    isAuthRequired: true,
    isVerificationRequired: true,
}), [endpointMessage, query]);
/** Mutation api for creating a new message **attached to a vuln report, not a conversation**.  */
export const useMessagePostSingleApi = () => useMessageServiceMutationApi('SendMessage', [endpointMessage]);
/** React-query mutation API for creating individual **replies** to a conversation*/
export const useConversationPostReplySingleApi = (conversationId) => useOdataPostSingleApi(endpointMessage, [
    endpointConversation,
    getEndpointConversationMessage(conversationId),
]);
/** Helper function for building React-query mutation APIs against `MessageService` */
const useMessageServiceMutationApi = (serviceName, additionalEndpoints = []) => useOdataPostSingleApi(`${endpointMessage}/MessageService.${serviceName}`, [
    ...additionalEndpoints,
    `${endpointMessage}/MessageService.${serviceName}`,
    endpointVuln,
]);
/**
 * Helper function for building React-query mutation APIs against `MessageService`
 *  with endpoint contains variable eg. key such as messageId
 */
const useMessageServiceSingleMessageMutationApi = (serviceName, endpoint) => useOdataPostSingleApi(`${endpoint}/MessageService.${serviceName}`, [`${endpoint}/MessageService.${serviceName}`, endpointVuln]);
/** React-query mutation API for marking a batch of messages as **read*/
export const useMessageMarkAsReadMutationApi = (conversationId) => useMessageServiceMutationApi(`MarkAsRead`, [
    endpointMessage,
    getEndpointConversationMessage(conversationId),
]);
/**
 *  React-query mutation API for marking a batch of messages as **unread**.
 */
export const useMessageMarkAsUnreadMutationApi = (conversationId) => useMessageServiceMutationApi(`MarkAsUnread`, [
    endpointMessage,
    getEndpointConversationMessage(conversationId),
]);
/**
 *  React-query mutation API for marking a batch of messages as **flagged**.
 */
export const useMessageFlagMutationApi = () => useMessageServiceMutationApi(`Flag`, [
    endpointConversation,
    endpointMessage,
]);
/**
 *  React-query mutation API for marking a batch of messages as **unflagged**.
 */
export const useMessageUnflagMutationApi = () => useMessageServiceMutationApi(`Unflag`, [
    endpointConversation,
    endpointMessage,
]);
/**
 *  React-query mutation API for marking all messages within a conversation as **unflagged**.
 */
export const useMessageUnflagAllInConversationMutationApi = () => useMessageServiceMutationApi(`UnflagByConversation`, [endpointConversation, endpointMessage, endpointVuln]);
/** Hybrid mutation api that lets you send (id, boolean) as params and automagically swap apis */
export const useMessageFlagUnflagMutationApi = () => {
    const flag = useMessageFlagMutationApi();
    const unflag = useMessageUnflagMutationApi();
    return {
        isLoading: flag.isLoading || unflag.isLoading,
        error: flag.error || unflag.error,
        mutateAsync: (messageId, isFlaggedNewValue) => {
            if (isFlaggedNewValue) {
                return flag.mutateAsync({ messageIds: [messageId] });
            }
            else {
                return unflag.mutateAsync({ messageIds: [messageId] });
            }
        },
    };
};
/** React-query mutation API for marking a batch of messages as **require response*/
export const useMessageResponseRequireMutationApi = (messageId) => useMessageServiceSingleMessageMutationApi('RequireResponse', getEndpointMessage(messageId));
